import { type TokenId, TOKEN_IDS } from "@/types";
import { useDialogPortal, useTransactionFees, useStakingAPY, useProtocolStore } from "@/hooks";
import { depositEventEmitter, DEPOSIT_EVENTS } from "@/events";
import {
    gatewayMintAndStakeTransaction,
    estimateMintAndStakeTransactionGas,
    prepareMintAndStakeTransaction,
    prepareVaultDepositTransaction,
    estimateVaultDepositTransactionGas,
    gatewayVaultDepositTransaction,
    prepareZapInAndStakeTransaction,
    estimateZapInAndStakeTransactionGas,
    gatewayZapInAndStakeTransaction,
} from "@/services";
import { useGasEstimation } from "@/hooks";
import { useTransactionExecution } from "@/hooks";
import { AppError } from "@/types";
import { useCallback, useMemo } from "react";
import { type Address, erc20Abi, type WalletClient, parseUnits } from "viem";
import { useTokenApprovalV2 } from "./useTokenApprovalV2";
import { QUERY_KEYS, TOKEN_ADDRESSES, TOKEN_CONFIG, getGatewayContractAddress, publicClients } from "@/config";
import { mainnet } from "viem/chains";
import { useAccount } from "wagmi";
import { useQueryClient } from "@tanstack/react-query";

interface DepositTransactionProps {
    depositAmount: number;
    depositTokenId: TokenId;
}

export function useDepositTransaction({ depositAmount, depositTokenId }: DepositTransactionProps) {
    const { address: owner } = useAccount();
    const depositAmountWei = useMemo(
        () => parseUnits(`${depositAmount}`, TOKEN_CONFIG[depositTokenId].decimals),
        [depositAmount, depositTokenId],
    );

    const { closeDialog } = useDialogPortal();

    const { isApproving: isTokenApproving, isApproved } = useTokenApprovalV2({
        spender: getGatewayContractAddress(),
        tokenId: depositTokenId,
        amount: depositAmount,
    });

    const { isApproving: isVaultApproving, isApproved: hasVaultApproval } = useTokenApprovalV2({
        spender: getGatewayContractAddress(),
        tokenId: TOKEN_IDS.IUSD,
        amount: depositAmount,
    });

    const { apy } = useStakingAPY(Number(depositAmount));
    const { stats, isLoading } = useProtocolStore();

    const estimateGasFn = useCallback(
        (walletClient: WalletClient) => {
            if (depositTokenId === TOKEN_IDS.USDC) {
                return estimateMintAndStakeTransactionGas(depositAmount, walletClient);
            }
            if (depositTokenId === TOKEN_IDS.IUSD) {
                return estimateVaultDepositTransactionGas(depositAmount, walletClient);
            }
            if (depositTokenId === TOKEN_IDS.USDT) {
                return estimateZapInAndStakeTransactionGas(
                    depositTokenId,
                    depositAmount,
                    walletClient.account?.address || "",
                    walletClient,
                );
            }
            if (depositTokenId === TOKEN_IDS.SUSDE) {
                return estimateZapInAndStakeTransactionGas(
                    depositTokenId,
                    depositAmount,
                    walletClient.account?.address || "",
                    walletClient,
                );
            }

            throw new AppError("Invalid token for deposit");
        },
        [depositAmount, depositTokenId],
    );

    const prepareTransactionFn = useCallback(
        async (walletClient: WalletClient) => {
            if (depositTokenId === TOKEN_IDS.USDC) {
                return prepareMintAndStakeTransaction(depositAmount, walletClient);
            }
            if (depositTokenId === TOKEN_IDS.IUSD) {
                return prepareVaultDepositTransaction(depositAmount, walletClient);
            }
            if (depositTokenId === TOKEN_IDS.USDT) {
                return prepareZapInAndStakeTransaction(
                    depositTokenId,
                    depositAmount,
                    walletClient.account?.address || "",
                    walletClient,
                );
            }
            if (depositTokenId === TOKEN_IDS.SUSDE) {
                return prepareZapInAndStakeTransaction(
                    depositTokenId,
                    depositAmount,
                    walletClient.account?.address || "",
                    walletClient,
                );
            }
            throw new AppError("Invalid token for deposit");
        },
        [depositAmount, depositTokenId],
    );

    const {
        gasEstimate,
        isEstimatingGas,
        error: gasError,
    } = useGasEstimation({
        estimateGasFn,
        prepareTransactionFn,
        hasApproval: depositTokenId === TOKEN_IDS.IUSD ? hasVaultApproval : isApproved === true,
        tokenId: depositTokenId,
    });

    const { gasPriceUSD, slippagePercent } = useTransactionFees({
        estimatedGas: gasEstimate,
    });

    const executeTransactionFn = useCallback(
        async (walletClient: WalletClient) => {
            if (depositTokenId === TOKEN_IDS.USDC) {
                return gatewayMintAndStakeTransaction(depositAmount, walletClient);
            }
            if (depositTokenId === TOKEN_IDS.IUSD) {
                return gatewayVaultDepositTransaction(depositAmount, walletClient);
            }
            if (depositTokenId === TOKEN_IDS.USDT) {
                return gatewayZapInAndStakeTransaction(
                    depositTokenId,
                    depositAmount,
                    walletClient.account?.address || "",
                    walletClient,
                );
            }
            if (depositTokenId === TOKEN_IDS.SUSDE) {
                return gatewayZapInAndStakeTransaction(
                    depositTokenId,
                    depositAmount,
                    walletClient.account?.address || "",
                    walletClient,
                );
            }
            if (depositTokenId === TOKEN_IDS.USDE) {
                return gatewayZapInAndStakeTransaction(
                    depositTokenId,
                    depositAmount,
                    walletClient.account?.address || "",
                    walletClient,
                );
            }

            throw new AppError("Invalid token for deposit");
        },
        [depositAmount, depositTokenId],
    );

    const { invalidateQueries } = useQueryClient();

    const {
        transactionHash,
        isExecuting,
        error: transactionError,
        executeTransaction,
    } = useTransactionExecution({
        executeTransactionFn,
        onSuccess: (hash) => {
            depositEventEmitter.emit(DEPOSIT_EVENTS.SUCCESS, {
                transactionHash: hash,
                depositTokenId,
                depositAmount,
            });
            closeDialog();
            invalidateQueries({
                queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(owner),
            });
        },
        errorMessage: "Failed to deposit tokens",
    });

    const { siUSDExchange } = (() => {
        if (isLoading) {
            return { siUSDExchange: 0 };
        }

        const siUSDExchange = stats.staked.exchangeRateNormalized;

        return { siUSDExchange };
    })();

    const executeDepositTransaction = useCallback(async () => {
        if (!owner) throw new Error("Owner address is required");

        const multicallResponse = publicClients[mainnet.id].multicall({
            contracts: [
                {
                    address: TOKEN_ADDRESSES[mainnet.id][depositTokenId] as Address,
                    abi: erc20Abi,
                    functionName: "balanceOf",
                    args: [owner],
                },
                {
                    address: TOKEN_ADDRESSES[mainnet.id][depositTokenId] as Address,
                    abi: erc20Abi,
                    functionName: "allowance",
                    args: [owner, getGatewayContractAddress(mainnet.id) as Address],
                },
            ],
        });

        if (!multicallResponse) throw new Error("Failed to get token balance and allowance");

        const [balance, allowance] = await multicallResponse;

        if (balance.error || allowance.error) throw new Error("Failed to get token balance or allowance");

        // Check if the user has enough funds in their wallet
        if (balance.result < depositAmountWei) {
            throw new Error("Insufficient token balance", {
                cause: {
                    balance: balance.result,
                    required: depositAmountWei,
                    error: balance.error,
                },
            });
        }

        // Check if the user has approved the gateway contract to spend their tokens
        if (allowance.result < depositAmountWei) {
            throw new Error("Insufficient token allowance", { cause: allowance.error });
        }

        // Execute transaction
        return executeTransaction();
    }, [depositAmountWei, depositTokenId, executeTransaction, owner, depositAmountWei]);

    return {
        gasEstimate,
        gasPriceUSD,
        slippagePercent,
        siUSDExchange,
        apy: !apy || String(apy) === "0" ? 5.7 : String(apy),
        transactionHash,
        executeDepositTransaction,
        isLoading: isExecuting || isTokenApproving || isVaultApproving,
        isEstimatingGas,
        needsApproval: depositTokenId === TOKEN_IDS.IUSD ? !hasVaultApproval : !isApproved,
        error: transactionError,
        gasError,
        isExecuting,
    };
}
